/**
 * --------------------------------------------------------------------------
 * CoreUI PRO data-grid engine/features.ts
 * License (https://coreui.io/pro/license/)
 * --------------------------------------------------------------------------
 */

import {
  cellSelectionFeature,
  columnFacetingFeature,
  columnFilteringFeature,
  columnOrderingFeature,
  columnPinningFeature,
  columnResizingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createFacetedRowModel,
  createFacetedUniqueValues,
  createFilteredRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  filterFn_includesString as includesStringFilterFn,
  globalFilteringFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  sortFn_alphanumeric as alphanumericSortFn,
  sortFn_datetime as datetimeSortFn,
  sortFn_text as textSortFn,
  tableFeatures
} from '@tanstack/table-core'
import { storeReactivityBindings } from '@tanstack/table-core/store-reactivity-bindings'

// v9 exposes an API only when its feature is registered, so this is the full
// set of table-core behavior the grid can reach - options gate the behavior,
// not the registration. Prerequisites must precede dependents: sizing before
// resizing, filtering before global filtering and faceting, every row-model
// slot after its feature.
export const dataGridFeatures = tableFeatures({
  coreReactivityFeature: storeReactivityBindings(),
  cellSelectionFeature,
  columnFilteringFeature,
  globalFilteringFeature,
  columnFacetingFeature,
  columnOrderingFeature,
  columnPinningFeature,
  columnSizingFeature,
  columnResizingFeature,
  columnVisibilityFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  facetedRowModel: createFacetedRowModel(),
  facetedUniqueValues: createFacetedUniqueValues(),
  filteredRowModel: createFilteredRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
  sortedRowModel: createSortedRowModel(),
  filterFns: { includesString: includesStringFilterFn },
  // The names column.getAutoSortFn() resolves for sampled value types; without
  // them every column would silently fall back to the basic comparator.
  sortFns: {
    alphanumeric: alphanumericSortFn,
    datetime: datetimeSortFn,
    text: textSortFn
  }
})

export type DataGridFeatures = typeof dataGridFeatures
